import { fetchAuthSession } from 'aws-amplify/auth';
import awsConfig from '../config/aws-config';
import familyAPIService from './familyAPI';

class ApiService {
  constructor() {
    this.baseURL = awsConfig.apiGateway.URL;
  }

  /**
   * 認証ヘッダー取得（外部テスト用にも使用・トークンリフレッシュ対応）
   */
  async getAuthHeaders() {
    try {
      console.log('Fetching auth session...');
      const session = await fetchAuthSession({ forceRefresh: true });
      console.log('Auth session (force refreshed):', session);
      
      const token = session.tokens?.idToken?.toString();
      console.log('ID Token available:', !!token);
      
      if (!token) {
        console.error('No ID token found in session');
        throw new Error('認証トークンが見つかりません');
      }
      
      // トークンの有効期限をチェック
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        
        if (payload.exp && payload.exp <= currentTime) {
          console.warn('Token is expired, attempting force refresh...');
          const refreshedSession = await fetchAuthSession({ forceRefresh: true });
          const refreshedToken = refreshedSession.tokens?.idToken?.toString();
          
          if (!refreshedToken) {
            throw new Error('認証トークンのリフレッシュに失敗しました');
          }
          
          console.log('Token successfully refreshed');
          
          const headers = {
            'Authorization': `Bearer ${refreshedToken}`,
            'Content-Type': 'application/json'
          };
          
          console.log('Generated auth headers (refreshed):', { ...headers, 'Authorization': 'Bearer [REDACTED]' });
          return headers;
        }
      } catch (parseError) {
        console.warn('Could not parse token for expiry check:', parseError);
      }
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      console.log('Generated auth headers:', { ...headers, 'Authorization': 'Bearer [REDACTED]' });
      return headers;
    } catch (error) {
      console.error('Error getting auth headers:', error);
      throw new Error('認証情報の取得に失敗しました');
    }
  }

  /**
   * API リクエスト実行
   */
  async makeRequest(endpoint, options = {}) {
    try {
      console.log(`makeRequest: Starting request to ${endpoint}`);
      const authHeaders = await this.getAuthHeaders();
      
      const config = {
        ...options,
        headers: {
          ...authHeaders,
          ...(options.headers || {})
        }
      };

      const fullUrl = `${this.baseURL}${endpoint}`;
      console.log('makeRequest: Full URL:', fullUrl);
      console.log('makeRequest: Config:', config);

      const response = await fetch(fullUrl, config);
      console.log('makeRequest: Response status:', response.status);
      console.log('makeRequest: Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('makeRequest: Error response:', errorData);
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      // レスポンスが空の場合（204 No Content等）
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        console.log('makeRequest: Empty response (204 or no content)');
        return null;
      }

      const responseData = await response.json();
      console.log('makeRequest: Response data:', responseData);
      return responseData;
    } catch (error) {
      console.error('makeRequest: Request failed:', error);
      throw error;
    }
  }

  // 個人タスク関連の関数は削除されました（個人タスク機能廃止のため）

  /**
   * ヘルスチェック
   */
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseURL}/tasks`, {
        method: 'OPTIONS'
      });
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }

  /**
   * ユーザー設定取得
   */
  async getUserSettings() {
    const response = await this.makeRequest('/settings', { method: 'GET' });
    return response?.settings || {};
  }

  /**
   * ユーザー設定保存
   */
  async saveUserSettings(settingsData) {
    const response = await this.makeRequest('/settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData)
    });
    return response?.settings || {};
  }
}

// シングルトンインスタンス
const apiService = new ApiService();

// ファミリーアカウント関連API（新設計のfamilyAPIServiceを使用）
export const familyGroupsAPI = {
  // ユーザーの所属ファミリーアカウント一覧取得
  getFamilyGroups: async () => {
    return await familyAPIService.getFamilyGroups();
  },

  // 新しいファミリーアカウント作成
  createFamilyGroup: async (familyName, description = '') => {
    return await familyAPIService.createFamilyGroup(familyName, description);
  },

  // 招待トークン生成（既存グループ用）
  generateInviteToken: async (groupName) => {
    console.log('🔄 generateInviteToken called with groupName:', groupName);
    
    try {
      const response = await familyAPIService.generateInviteToken(groupName);
      console.log('✅ generateInviteToken success:', response);
      return response;
    } catch (error) {
      console.error('❌ generateInviteToken error:', error);
      throw error;
    }
  },

  // 招待トークンでファミリーアカウントに参加
  joinByInviteToken: async (inviteToken) => {
    return await familyAPIService.joinByInviteToken(inviteToken);
  },

  // 既存ファミリーアカウントに参加（旧方式・互換性のため）
  joinFamilyGroup: async (groupName, inviteCode = '') => {
    return await familyAPIService.makeRequest('/family-groups', {
      method: 'PUT',
      body: JSON.stringify({
        groupName,
        inviteCode
      })
    });
  },

  // ファミリーアカウントから脱退
  leaveFamilyGroup: async (groupId) => {
    return await familyAPIService.leaveFamilyGroup(groupId);
  },

  // ファミリーアカウントから他のメンバーを削除（オーナーのみ）
  removeMember: async (groupName, memberUserId) => {
    return await familyAPIService.removeMember(groupName, memberUserId);
  },

  // ファミリーアカウントのタスク取得
  getFamilyTasks: async (groupName) => {
    return await familyAPIService.getFamilyTasks(groupName);
  },

  // ファミリーアカウントの設定取得
  getFamilySettings: async (groupName) => {
    return await familyAPIService.getFamilySettings(groupName);
  },

  // ファミリーアカウントの設定更新
  updateFamilySettings: async (groupName, settings) => {
    return await familyAPIService.updateFamilySettings(groupName, settings);
  },

  // ファミリーアカウント名変更
  updateFamilyName: async (groupName, familyName) => {
    return await familyAPIService.makeRequest(`/family-groups/${encodeURIComponent(groupName)}/name`, {
      method: 'PUT',
      body: JSON.stringify({ familyName })
    });
  },

  // Family ID利用可能性チェック
  checkFamilyIdAvailability: async (familyId) => {
    return await familyAPIService.checkFamilyIdAvailability(familyId);
  }
};

export default apiService; 